(ns practitest.frontend.common-old-fe.ui
  (:require
    ["flatpickr" :as flatpickr] ;used by ET for date time picker
    ["react-datetime" :as react-datetime]
    [reagent.dom :as rdom]
    [clojure.string :as string]
    [practitest.frontend.common-old-fe.logging :as log]
    [reagent.core :as reagent]
    [reagent.dom.server :as rds]))

(def datetime (reagent/adapt-react-class react-datetime))

(def initial-focus-wrapper
  "Use this to wrap the element you want to receive focus."
  (with-meta identity
             {:component-did-mount (fn [this]
                                     (let [node (rdom/dom-node this)]
                                       (when (aget node "setSelectionRange")
                                         (.setSelectionRange node 0 (-> node .-value .-length)))
                                       (.focus node)))}))

(defn button [on-click label]
  [:button.btn.btn-default
   {:type     :button
    :on-click (fn [_]
                (on-click)
                nil)}
   label])

(defn read-only-text [value]
  [:input.form-control {:type :text :read-only true :value value}])

(defn writable-text [value on-change]
  [:input.form-control
   {:type      :text
    :value     value
    :on-change (fn [e]
                 (on-change (-> e .-target .-value))
                 nil)}])

(defn read-only-memo [value]
  [:textarea.form-control {:rows 3 :read-only true :value value}])

(defn writable-memo [value on-change]
  [:textarea.form-control
   {:rows      3
    :value     value
    :on-change (fn [e]
                 (on-change (-> e .-target .-value))
                 nil)}])

(defn writable-select [multi? options value on-change]
  [initial-focus-wrapper
   [:select.form-control
    {:value     (or @value (if multi? [] ""))
     :multiple  multi?
     :on-change (fn [e]
                  (let [node (.-target e)
                        selected (if multi?
                                   (->> (array-seq (.-options node))
                                        (filter #(.-selected %))
                                        (map #(.-value %))
                                        vec)
                                   (.-value node))]
                    (reset! value selected)
                    (on-change selected)
                    nil))}
    (for [[i [v l]] (map-indexed vector options)]
      ^{:key i} [:option {:value v :title l} l])]])

(defn read-only-checkbox [value]
  [:div.checkbox
   [:label [:input {:type :checkbox :checked value :read-only true}]]])

(defn writable-checkbox [value on-change]
  [:div.checkbox
   [:label [:input {:type      :checkbox
                    :checked   value
                    :on-change (fn [e]
                                 (on-change (-> e .-target .-checked))
                                 nil)}]]])

(defn editable-control [opts view-control edit-control on-save]
  (let [editing? (reagent/atom false)]
    (fn [opts view-control edit-control save-fn]
      [:div {:class (:additional-classes opts)}
       (if (:new? opts)
         edit-control
         (if @editing?
           [:div.input-group
            edit-control
            [:div.input-group-btn.ui-editable-buttons
             [button
              (fn []
                (on-save)
                (reset! editing? false))
              [:i.fa.fa-check]]
             [button #(reset! editing? false) [:i.fa.fa-close]]]]
           [:div.input-group
            view-control
            [:div.input-group-btn.ui-editable-buttons
             [button #(reset! editing? true) [:i.fa.fa-pencil]]]]))])))

(defn editable-text [{:keys [value on-change on-save] :as opts}]
  [editable-control
   opts
   [read-only-text value]
   [writable-text value on-change]
   on-save])

(defn editable-memo [{:keys [value on-change on-save] :as opts}]
  [editable-control
   opts
   [read-only-memo value]
   [writable-memo value on-change]
   on-save])

(defn editable-select [{:keys [value options multi? on-change on-save] :as opts}]
  (let [editing? (reagent/atom false)
        state (reagent/atom value)]
    (fn [{:keys [value options multi? on-change on-save] :as opts}]
      [:div {:class (:additional-classes opts)}
       (if (:new? opts)
         [writable-select multi? options state on-change]
         (if @editing?
           [:div.input-group
            [writable-select multi? options state on-change]
            [:div.input-group-btn.ui-editable-buttons
             [button
              (fn []
                (on-save @state)
                (reset! editing? false))
              [:i.fa.fa-check]]
             [button
              (fn []
                (reset! state value)
                (reset! editing? false))
              [:i.fa.fa-close]]]]
           [:div.input-group
            (let [display-value (if (vector? value)
                                  (string/join "; " (map #(get options %) value))
                                  (get options value ""))]
              [:textarea.form-control.et-small-text-area
               {:read-only true
                :rows      1
                :value     display-value
                :title     display-value}])
            [:div.input-group-btn.ui-editable-buttons
             [button #(reset! editing? true) [:i.fa.fa-pencil]]]]))])))

(defn editable-checkbox [{:keys [value on-change on-save] :as opts}]
  [editable-control
   opts
   [read-only-checkbox value]
   [writable-checkbox value on-change]
   on-save])

(defn editable-datetime [{:keys [value on-change on-save] :as opts}]
  [editable-control
   opts
   [read-only-text (if (string/blank? value)
                     value
                     (.format (js/moment value) "L"))]
   [datetime
    {:value      (if (string/blank? value)
                   value
                   (js/moment value))
     :open       true
     :class-name "ui-editable-datepicker"
     :on-change  (fn [v]
                   (when-not (string? v)
                     (on-change (.toDate v)))
                   nil)}]
   on-save])

(defn editable-datetime2 [{:keys [value on-change] :as opts}]
  (reagent/create-class
    {:reagent-render      (fn []
                            [:input.form-control {:type :text}])
     :component-did-mount (fn [comp]
                            (let [node (rdom/dom-node comp)]
                              (js/flatpickr node (clj->js {:enableTime  true
                                                           :dateFormat  "Y-m-d H:i"
                                                           :time_24hr   true
                                                           :defaultDate (js/moment value)
                                                           :onChange    (fn [selected-dates date-str instance]
                                                                          (on-change (aget selected-dates 0)))}))))
     :display-name        "DateTime"}))

(defn info-tooltip [{:keys [options]} & content]
  (let [{:keys [style placement]
         :or   {style     {:padding-bottom "2px"}
                placement "right"}} options]
    (reagent/create-class
      {:reagent-render      (fn []
                              [:i.fa.fa-info-circle.fa-fw.clickable
                               {:style          style
                                :data-content   (rds/render-to-static-markup
                                                  (into [:<>] content))
                                :data-placement placement}])
       :component-did-mount (fn [comp]
                              (let [node (rdom/dom-node comp)]
                                (js-invoke (js/$ node) "popover"
                                           (clj->js {:html true :container "body"}))))})))
